import { forumService } from "../services/forum.service";

/**
 * Returns a debounced async (query) -> users[] function suitable for the
 * MarkdownEditor's `searchUsers` prop.
 *
 * Why we need this: Tiptap's Suggestion plugin invokes `items` on every
 * keystroke. Without protection that's one HTTP call per character, plus
 * an out-of-order race where a stale slow response can overwrite a newer
 * fast response.
 *
 * What this does:
 *  1. Debounces by `delayMs` so a fast typist only triggers the fetch
 *     once they've paused.
 *  2. Drops stale results: if a newer query landed while the previous
 *     fetch was in flight, the older promise resolves to [] instead of
 *     showing the old user list.
 *  3. Resolves immediately to [] for empty queries so the popup shows
 *     "no matches" instead of pulling the whole tenant.
 *
 * Each caller (NewThreadModal / CommentItem / ThreadDetailView) gets its
 * own debounced function via this factory so unmounting one editor
 * doesn't affect the others.
 */
export function createMentionSearch({ productVariantId = null, limit = 8, delayMs = 300 } = {}) {
  let timer = null;
  let lastQuery = null;

  // Backend UserInfoModel serializes as { id, username, firstName,
  // lastName, email, type } (camelCase). `type` is "Student" / "Trainer"
  // / "Management" — same naming as the rest of the app's User.Type
  // column. Single shape, no fallbacks.
  // Coerce only primitive values from the backend row. Anything non-string /
  // non-number (DOM node, undefined, plain object) becomes "" so the
  // downstream `[firstName, lastName].join(" ")` can never produce
  // "[object Comment]" or "[object Object]" via implicit toString.
  const coerceToString = (rawValue) =>
    (typeof rawValue === "string" || typeof rawValue === "number") ? String(rawValue) : "";

  const mapUserRow = (userRow) => {
    const firstName = coerceToString(userRow && userRow.firstName).trim();
    const lastName  = coerceToString(userRow && userRow.lastName).trim();
    const fullName  = [firstName, lastName].filter(Boolean).join(" ").trim();
    return {
      id:   userRow && userRow.id != null ? userRow.id : null,
      name: fullName || coerceToString(userRow && userRow.username) || coerceToString(userRow && userRow.email) || "Unnamed user",
      type: coerceToString(userRow && userRow.type),
      kind: "user",
    };
  };

  return function search(query) {
    const trimmedQuery = (query || "").trim();
    lastQuery = trimmedQuery;

    return new Promise((resolve) => {
      if (timer) clearTimeout(timer);

      // Tiptap calls items() with empty query when the popup first opens.
      // Returning [] keeps the popup empty + no network call.
      if (!trimmedQuery) {
        resolve([]);
        return;
      }

      timer = setTimeout(async () => {
        // Bail if the user kept typing while the timer was waiting.
        if (trimmedQuery !== lastQuery) {
          resolve([]);
          return;
        }
        try {
          const userRows = await forumService.searchMentions({
            productVariantId,
            searchQuery: trimmedQuery,
            limit,
          });
          // Final guard: response could come back after a newer query.
          if (trimmedQuery !== lastQuery) {
            resolve([]);
            return;
          }
          // Filter out non-plain-object rows (DOM nodes, primitives,
          // anything missing an id) before mapping. Belt-and-braces with
          // mapUserRow's per-field coercion.
          const validUsers = (Array.isArray(userRows) ? userRows : [])
            .filter((userRow) => userRow && typeof userRow === "object" && userRow.nodeType === undefined && userRow.id != null)
            .map(mapUserRow)
            .filter((mappedUser) => mappedUser.id != null);
          resolve(validUsers);
        } catch (searchError) {
          resolve([]);
        }
      }, delayMs);
    });
  };
}
